/**
 * SentimentSplitWidget — one 100% stacked horizontal bar per series (e.g.
 * Inbound / Outbound, or Agent / Customer), segments ordered Positive →
 * Negative. Plain React/CSS, no Highcharts, matching the approach in
 * BarDrilldownCustomWidget (nothing to keep in sync with a canvas/SVG
 * container).
 *
 * chart_type: "sentiment_split".
 *
 * ── API shape ──────────────────────────────────────────────────────────────
 * `data: { categories: string[], series: [{ name, data: number[] }] }` —
 * `categories` is the shared sentiment scale (Positive → Negative) and each
 * series' `data` is raw counts, index-aligned to `categories`. No per-cell
 * percentage is sent; share is always computed as value / series-total.
 * (Confirmed live: earlier draft of this file assumed a flat array of
 * `"Group - Level"`-named rows — that was a different, incorrect payload
 * shape seen before the backend settled on this one.)
 *
 * ── Non-obvious calls ──────────────────────────────────────────────────────
 *
 * 1. Each series' total is SUMMED from its own `data` rather than read from
 *    a metadata key, so nothing has to be guessed from the series name —
 *    works the same whether the two series happen to sum to the grand total
 *    (e.g. Inbound + Outbound) or each independently cover the full
 *    population (e.g. Agent / Customer both rating the same calls).
 *
 * 2. Inline labels are rounded to whole percent and always drawn, even on a
 *    segment too narrow to hold the text — the label is centered and
 *    absolutely positioned (see .scss) so it overflows onto neighboring
 *    segments rather than getting clipped or silently omitted.
 *
 * 3. Label ink inside a segment is picked from the fill's luminance, so it
 *    always clears contrast — the one case where text may sit on a data
 *    color.
 *
 * 4. Category → level matching goes through the shared `sentimentScale.ts`
 *    (also used by DonutPieWidget), so the same category is never a
 *    different color in two places on the same dashboard.
 */

import { useMemo, useState } from "react";

import { toSafeNumber } from "src/charts/data/transformers";
import {
  SENTIMENT_LEVELS as LEVELS,
  sentimentRank,
} from "./sentimentScale";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";
import "./SentimentSplitWidget.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

type SentimentSplitPayload = {
  categories?: unknown[];
  series?: Array<{ name?: unknown; data?: unknown[] }>;
};

type Metadata = {
  total_scored_calls?: number;
};

type Props = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

type Segment = {
  levelKey: string;
  label: string;
  color: string;
  value: number;
  /** Share printed on screen — the payload's own figure when it supplies one. */
  pct: number;
  /** Geometric width, always summing to 100 across the row. */
  widthPct: number;
};

type GroupRow = {
  group: string;
  total: number;
  segments: Segment[];
};

// ─── Scale ────────────────────────────────────────────────────────────────────

const UNKNOWN_COLOR = "#b8b6ad";

// ─── Helpers ──────────────────────────────────────────────────────────────────

type UnwrapResult = {
  payload: SentimentSplitPayload;
  metadata: Metadata;
  // Siblings of .data, not nested under .metadata — same envelope shape as
  // CustomBarWidget/InfoBarWidget's own unwrap.
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

const unwrap = (raw: unknown): UnwrapResult => {
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    // Full API response: { data: { categories, series }, metadata }.
    if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
      return {
        payload: obj.data as SentimentSplitPayload,
        metadata: (obj.metadata ?? {}) as Metadata,
        hasDownloadOptions: obj.has_download_options as boolean | undefined,
        downloadOptions: obj.download_options as
          | WidgetDownloadOptions
          | undefined,
      };
    }
    // WidgetRenderer may already have stripped the envelope, leaving
    // { categories, series } directly as `data`.
    if (Array.isArray((obj as SentimentSplitPayload).categories)) {
      return { payload: obj as SentimentSplitPayload, metadata: {} };
    }
  }
  return { payload: {}, metadata: {} };
};

/** Relative luminance (WCAG) — decides white vs ink for a label sitting on a
 *  colored fill, the one place text is allowed to wear a data color. */
const isDarkFill = (hex: string): boolean => {
  const h = hex.replace("#", "");
  if (h.length !== 6) return true;
  const channel = (offset: number): number => {
    const v = parseInt(h.slice(offset, offset + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance =
    0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance < 0.4;
};

const formatNum = (value: number): string => value.toLocaleString();
const formatPct = (value: number): string => `${value.toFixed(1)}%`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function SentimentSplitWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: Props) {
  const { payload, metadata, hasDownloadOptions, downloadOptions } = useMemo(
    () => unwrap(data),
    [data],
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={hasDownloadOptions ? downloadOptions : undefined}
    />
  );

  const groups = useMemo<GroupRow[]>(() => {
    const categories = (payload.categories ?? []).map((c) => String(c));
    const seriesList = payload.series ?? [];

    // Preserve payload order (Inbound before Outbound, Agent before
    // Customer, ...) rather than sorting — it's the intended reading order.
    return seriesList.map((s) => {
      const group = String(s.name ?? "");
      const values = categories.map((_, i) => toSafeNumber(s.data?.[i]));
      // Series total is summed from its own values rather than read from a
      // metadata key, so nothing has to be guessed from the series name —
      // works whether the series' totals sum to the grand total (e.g.
      // Inbound + Outbound) or each independently cover the full population
      // (e.g. Agent / Customer both rating the same calls).
      const total = values.reduce((sum, v) => sum + v, 0);

      // Order segments by the canonical sentiment scale, keeping anything
      // unrecognized at the end in payload order.
      const known: Segment[] = [];
      const unknown: Segment[] = [];

      categories.forEach((category, i) => {
        const value = values[i] ?? 0;
        const level = LEVELS.find((candidate) => candidate.test(category));
        // No per-cell percentage is sent for this shape — share is always
        // value / series-total, so pct and widthPct are the same number.
        const pct = total > 0 ? (value / total) * 100 : 0;
        const segment: Segment = {
          levelKey: level?.key ?? category,
          label: level?.label ?? category,
          color: level?.color ?? UNKNOWN_COLOR,
          value,
          pct,
          widthPct: pct,
        };
        (level ? known : unknown).push(segment);
      });

      known.sort(
        (a, b) => sentimentRank(a.levelKey) - sentimentRank(b.levelKey),
      );

      return { group, total, segments: [...known, ...unknown] };
    });
  }, [payload]);

  // Legend covers every level actually present, in scale order.
  const legend = useMemo(() => {
    const seen = new Map<string, { label: string; color: string }>();
    for (const group of groups) {
      for (const segment of group.segments) {
        if (!seen.has(String(segment.levelKey))) {
          seen.set(String(segment.levelKey), {
            label: segment.label,
            color: segment.color,
          });
        }
      }
    }
    return [...seen.entries()].map(([key, value]) => ({ key, ...value }));
  }, [groups]);

  if (isLoading || data === undefined || data === null) {
    return (
      <div className="placeholder-glow h-100 w-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <>
        {downloadMenu}
        <p className="text-muted small mb-0">No sentiment data available.</p>
      </>
    );
  }

  const active = groups
    .flatMap((group) =>
      group.segments.map((segment) => ({ group: group.group, segment })),
    )
    .find(({ group, segment }) => `${group}:${segment.levelKey}` === activeKey);

  const scored = metadata.total_scored_calls;

  return (
    <div className="sentiment-split">
      {downloadMenu}
      <p className="sentiment-split__caption">
        {scored !== undefined
          ? `${formatNum(scored)} scored calls`
          : `${groups.length} groups · share of each group's own calls`}
      </p>

      <div className="sentiment-split__readout" aria-live="polite">
            {active ? (
              <>
                <span
                  className="sentiment-split__readout-swatch"
                  style={{ background: active.segment.color }}
                  aria-hidden="true"
                />
                <span className="sentiment-split__readout-name">
                  {active.group ? `${active.group} — ` : ""}
                  {active.segment.label}
                </span>
                <span className="sentiment-split__readout-meta">
                  {formatNum(active.segment.value)} calls ·{" "}
                  {formatPct(active.segment.pct)} of {active.group || "group"}
                </span>
              </>
            ) : (
              <span className="sentiment-split__readout-idle">
                Hover a segment for its exact count and share
              </span>
            )}
          </div>

          <div className="sentiment-split__rows">
            {groups.map((group) => (
              <div className="sentiment-split__row" key={group.group}>
                <div className="sentiment-split__row-head">
                  <span className="sentiment-split__row-label">
                    {group.group || "All"}
                  </span>
                  <span className="sentiment-split__row-n">
                    n={formatNum(group.total)}
                  </span>
                </div>

                <div
                  className="sentiment-split__bar"
                  role="img"
                  aria-label={`${group.group || "All"}: ${group.segments
                    .map(
                      (segment) =>
                        `${segment.label} ${formatPct(segment.pct)}`,
                    )
                    .join(", ")}`}
                >
                  {group.segments.map((segment) => {
                    const key = `${group.group}:${segment.levelKey}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        className="sentiment-split__seg"
                        style={{
                          // flex-basis carries the share; shrink is allowed
                          // (grow stays off) so the min-width floor in the
                          // .scss can be enforced by taking space from wider
                          // segments instead of overflowing the bar.
                          flex: `0 1 ${segment.widthPct}%`,
                          background: segment.color,
                        }}
                        // Focusable but not actionable — focus is how a keyboard
                        // reader reaches the readout. No drilldown exists here.
                        aria-disabled={true}
                        aria-label={`${segment.label}: ${formatNum(
                          segment.value,
                        )} calls, ${formatPct(segment.pct)}`}
                        onMouseEnter={() => setActiveKey(key)}
                        onMouseLeave={() =>
                          setActiveKey((k) => (k === key ? null : k))
                        }
                        onFocus={() => setActiveKey(key)}
                        onBlur={() =>
                          setActiveKey((k) => (k === key ? null : k))
                        }
                      >
                        <span
                          className="sentiment-split__seg-label"
                          style={{
                            color: isDarkFill(segment.color)
                              ? "#ffffff"
                              : "#1e293b",
                          }}
                        >
                          {Math.round(segment.pct)}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Six categories: identity must never rest on color alone. */}
          <div className="sentiment-split__legend">
            {legend.map((item) => (
              <span className="sentiment-split__legend-item" key={item.key}>
                <span
                  className="sentiment-split__legend-swatch"
                  style={{ background: item.color }}
                  aria-hidden="true"
                />
                {item.label}
              </span>
            ))}
          </div>
    </div>
  );
}
